
import { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Users, Camera, MapPin, Clock, Upload, Loader2 } from 'lucide-react';
import { buscaFacial } from "../lib/authFetch";
import { toast } from 'sonner';

interface BuscaMatch {
  detecao_id: string;
  similaridade_pct: number;
  camera_id: string;
  data_hora: string;
  localizacao?: string;
  reconhecido_como?: string;
  imagem_url: string;
  imagem_base64?: string;
}

const faceImgSrc = (item: { imagem_base64?: string; imagem_url?: string }) => {
  if (item.imagem_base64) {
    return item.imagem_base64.startsWith('data:')
      ? item.imagem_base64
      : `data:image/jpeg;base64,${item.imagem_base64}`;
  }
  if (item.imagem_url?.startsWith('data:')) return item.imagem_url;
  return undefined;
};

const FaceRecognition = () => {
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [buscaResultados, setBuscaResultados] = useState<BuscaMatch[]>([]);
  const [buscaMensagem, setBuscaMensagem] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const normalizeImageForSearch = (file: File): Promise<File> =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        try {
          const maxSide = 1600;
          let { width, height } = img;
          if (width < 32 || height < 32) {
            URL.revokeObjectURL(url);
            reject(new Error('Imagem muito pequena'));
            return;
          }
          if (Math.max(width, height) > maxSide) {
            const scale = maxSide / Math.max(width, height);
            width = Math.round(width * scale);
            height = Math.round(height * scale);
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            URL.revokeObjectURL(url);
            reject(new Error('Falha ao processar imagem'));
            return;
          }
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (blob) => {
              URL.revokeObjectURL(url);
              if (!blob) {
                reject(new Error('Falha ao converter imagem'));
                return;
              }
              resolve(
                new File([blob], (file.name || 'busca').replace(/\.\w+$/, '') + '.jpg', {
                  type: 'image/jpeg',
                })
              );
            },
            'image/jpeg',
            0.92
          );
        } catch (err) {
          URL.revokeObjectURL(url);
          reject(err);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Não foi possível ler a imagem. Use JPG ou PNG.'));
      };
      img.src = url;
    });

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Imagem maior que 10 MB');
      return;
    }
    try {
      const normalized = await normalizeImageForSearch(file);
      setUploadedFile(normalized);
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImage(e.target?.result as string);
      };
      reader.readAsDataURL(normalized);
      setBuscaResultados([]);
      setBuscaMensagem(null);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Erro ao carregar imagem');
    }
  };

  const handleBusca = async () => {
    if (!uploadedFile) return;
    setSearching(true);
    setBuscaResultados([]);
    setBuscaMensagem(null);
    try {
      const data = await buscaFacial(uploadedFile);
      setBuscaResultados(data.resultados || []);
      setBuscaMensagem(data.mensagem || null);
      if (data.total > 0) {
        toast.success(`${data.total} correspondência(s) encontrada(s)`);
      } else {
        toast.info(data.mensagem || 'Nenhuma correspondência no histórico');
      }
      window.dispatchEvent(new Event('atividade-registrada'));
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Erro na busca facial';
      toast.error(msg);
    } finally {
      setSearching(false);
    }
  };

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-protec-dark-light border-protec-gold/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-protec-gold" />
            Reconhecimento Facial
          </CardTitle>
          <CardDescription className="text-gray-300">
            Envie uma foto para buscar correspondências no histórico de capturas das câmeras
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="photo" className="text-gray-300">Foto para busca</Label>
              <div className="mt-2 border-2 border-dashed border-protec-gold/30 rounded-lg p-6 text-center hover:border-protec-gold/50 transition-colors">
                {uploadedImage ? (
                  <div className="space-y-4">
                    <img
                      src={uploadedImage}
                      alt="Foto enviada"
                      className="max-w-full h-48 object-contain mx-auto rounded-lg"
                    />
                    <Button
                      size="sm"
                      className="bg-protec-gold text-black hover:shadow-gold"
                      onClick={openFilePicker}
                    >
                      Trocar Foto
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-12 h-12 text-protec-gold mx-auto" />
                    <p className="text-gray-300">Clique para selecionar uma foto com rosto visível</p>
                    <p className="text-sm text-gray-400">PNG ou JPG, até 10 MB</p>
                  </div>
                )}
                <Input
                  ref={fileInputRef}
                  id="photo"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                {!uploadedImage && (
                  <Button
                    className="mt-4 bg-protec-gold text-black hover:shadow-gold"
                    onClick={openFilePicker}
                  >
                    Selecionar Arquivo
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-4 flex flex-col justify-center">
              <p className="text-gray-400 text-sm">
                O sistema compara o rosto da foto com capturas já registradas nas câmeras monitoradas.
                Quanto maior a similaridade, mais provável a correspondência.
              </p>
              <Button
                disabled={!uploadedFile || searching}
                className="w-full bg-protec-gold text-black hover:shadow-gold disabled:opacity-50"
                onClick={handleBusca}
              >
                {searching ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Buscando…
                  </>
                ) : (
                  <>
                    <Users className="w-4 h-4 mr-2" />
                    Iniciar Reconhecimento
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {(buscaResultados.length > 0 || buscaMensagem) && (
        <Card className="bg-protec-dark-light border-protec-gold/20">
          <CardHeader>
            <CardTitle className="text-white text-lg">Resultados da busca</CardTitle>
            {buscaMensagem && buscaResultados.length === 0 && (
              <CardDescription className="text-gray-400">{buscaMensagem}</CardDescription>
            )}
          </CardHeader>
          {buscaResultados.length > 0 && (
            <CardContent className="space-y-3">
              {buscaResultados.map((m) => {
                const src = faceImgSrc(m);
                return (
                  <div key={m.detecao_id} className="flex gap-4 bg-protec-dark rounded-lg p-4 border border-protec-gold/10">
                    {src ? (
                      <img src={src} alt="" className="w-20 h-20 object-cover rounded-lg border border-protec-gold/20" />
                    ) : (
                      <div className="w-20 h-20 rounded-lg bg-gray-800 flex items-center justify-center">
                        <Camera className="w-8 h-8 text-gray-600" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-white">
                          {m.reconhecido_como || 'Correspondência'}
                        </span>
                        <Badge className="bg-protec-gold/20 text-protec-gold">
                          {m.similaridade_pct}% similar
                        </Badge>
                      </div>
                      <p className="text-gray-400 text-sm mt-1">
                        <Clock className="inline w-4 h-4 mr-1 text-protec-gold" />
                        {new Date(m.data_hora).toLocaleString('pt-BR')}
                        <span className="mx-2">·</span>
                        <MapPin className="inline w-4 h-4 mr-1 text-protec-gold" />
                        {m.localizacao || 'Local não informado'}
                      </p>
                      <p className="text-gray-500 text-sm mt-1">
                        <Camera className="inline w-4 h-4 mr-1" />
                        Câmera {m.camera_id}
                      </p>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
};

export default FaceRecognition;
